import { inject, Injectable } from '@angular/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { IResponse } from '../../../../core/interfaces/IResponse';
import { ISessionBootstrap } from '../../../../core/interfaces/ISessionBootstrap';
import { IToken } from '../../../../core/interfaces/IToken';
import { ApiService } from '../../../../core/services/ApiService';
import { IEmployee } from '../../../employees/core/interfaces/IEmployee';
import { EmployeesService } from '../../../employees/core/services/EmployeesService';
import { TerminalsStore } from '../../../terminals/core/stores/TerminalsStore';
import { IIdentity } from '../interfaces/IIdentity';
import { ILoginWithTokenResult } from '../interfaces/ILoginWithTokenResult';
import { ILoginPayload } from '../payloads/ILoginPayload';
import { IdentityStore } from '../stores/IdentityStore';
import { AppStore } from '../../../../core/stores/AppStore';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { OtsStaffOrdersService } from '../../../ots-staff/core/services/OtsStaffOrdersService';
import { ReleaseNotesStore } from '../../../dashboard/core/stores/ReleaseNotesStore';
import { SupportModeStore } from '../../../support-mode/core/stores/SupportModeStore';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly _basePath = '/auth';
  private readonly _apiService = inject(ApiService);
  private readonly _identityStore = inject(IdentityStore);
  private readonly _terminalsStore = inject(TerminalsStore);
  private readonly _employeesService = inject(EmployeesService);
  private readonly _releaseNotesStore = inject(ReleaseNotesStore);
  private readonly _otsStaffOrdersService = inject(OtsStaffOrdersService);
  private readonly _supportModeStore = inject(SupportModeStore);

  public login(loginPayload: ILoginPayload): Observable<IResponse<IEmployee> | null> {
    return this._apiService.post(`${this._basePath}/signin`, loginPayload)
      .pipe(
        switchMap((res: IResponse<IIdentity>) => {
          this._identityStore.tmpIdentity.set({
            ...res.data,
            mustChangePin: !!res.data.mustChangePin,
          });

          if (res.data.mustChangePin) {
            return of(null);
          }

          return this._employeesService.getMyEmployee();
        })
      );
  }

  public loginWithToken(token: string): Observable<ILoginWithTokenResult> {
    return this._apiService.post(`${this._basePath}/login-with-token`, { token }).pipe(
      map(body => {
        const result = this.readLoginWithToken(body);

        if (!result) {
          throw new Error('InvalidLoginToken');
        }

        return result;
      })
    );
  }

  public stageTokenIdentity(result: ILoginWithTokenResult): void {
    this._identityStore.tmpIdentity.set({
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      expires: result.expires,
      mustChangePin: !!result.mustChangePin,
      mandator: {
        id: result.sessionBootstrap.mandatorId,
        name: '',
        token: '',
      },
    });
    this._identityStore.identity.set(null);
  }

  public restoreIdentity(identity: IIdentity | null): void {
    this._identityStore.tmpIdentity.set(null);
    this._identityStore.identity.set(identity);
  }

  public changePin(newPin: number, confirmPin: number): Observable<IResponse<unknown>> {
    return this._apiService.put(`${this._basePath}/pin`, { newPin, confirmPin });
  }

  public getHealthStatus(): Observable<IResponse<string>> {
    return this._apiService.get(`/health/status`);
  }

  public refreshToken(): Observable<IToken> {
    return this._apiService.post(
      `${this._basePath}/refresh-token`,
      {
        accessToken: this._identityStore.identity().accessToken,
        refreshToken: this._identityStore.identity().refreshToken,
      }
    );
  }

  public setRealIdentity(): void {
    if (this._identityStore.tmpIdentity()) {
      this._identityStore.identity.set(this._identityStore.tmpIdentity());
      this._identityStore.tmpIdentity.set(null);
    }
  }

  public logout(forceLogout = false): void {
    const logout = (): void => {
      this._releaseNotesStore.clearSessionDismiss();
      this._otsStaffOrdersService.reset();
      this._supportModeStore.disable();
      this._identityStore.identity.set(null);
      this._terminalsStore.selectedTerminal.set(null);
      this._terminalsStore.changedCinema.set(null);
      AppStore.shouldPreventAppReload.set(false);
      location.href = '/auth/login'.langUrl();
    };

    if (forceLogout) {
      logout();
    } else {
      if (this._identityStore.identity()) {
        this._apiService.post(`${this._basePath}/logout`)
          .subscribe({
            next: logout,
            error: (error: ApiErrorResponse) => {
              if (error.status !== 401) {
                logout();
              }
            }
          });
      } else {
        logout();
      }
    }
  }

  private readLoginWithToken(body: IResponse<ILoginWithTokenResult> | ILoginWithTokenResult | null): ILoginWithTokenResult | null {
    if (!body) {
      return null;
    }

    const payload = 'sessionBootstrap' in body ? body : body.data;

    if (!payload?.accessToken || !payload.refreshToken || !payload.sessionBootstrap) {
      return null;
    }

    const bootstrap = this.readSessionBootstrap(payload.sessionBootstrap);

    if (!bootstrap) {
      return null;
    }

    return {
      accessToken: payload.accessToken,
      refreshToken: payload.refreshToken,
      expires: payload.expires,
      mustChangePin: payload.mustChangePin,
      sessionBootstrap: bootstrap,
    };
  }

  private readSessionBootstrap(bootstrap: ISessionBootstrap): ISessionBootstrap | null {
    const terminalId = Number(bootstrap.terminalId);
    const sessionId = Number(bootstrap.sessionId);
    const cinemaId = Number(bootstrap.cinemaId);
    const mandatorId = Number(bootstrap.mandatorId);

    if (![terminalId, sessionId, cinemaId, mandatorId].every(Number.isFinite)) {
      return null;
    }

    return {
      terminalId,
      sessionId,
      workerNumber: `${bootstrap.workerNumber ?? ''}`,
      cinemaId,
      mandatorId,
    };
  }
}
